import { useRef, useState } from 'react'
import { FileText, Upload, Copy, Download, Loader2, X, Check, FileUp } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface ConvertResult {
  markdown: string
  pageCount: number
  elapsedMs: number
  fileName: string
}

export default function KIPdfToMd() {
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ConvertResult | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [copied, setCopied] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const pickFile = (f: File | null | undefined) => {
    if (!f) return
    // PDF + Bilder erlauben (PaddleOCR kann beides)
    const ok =
      f.type === 'application/pdf' ||
      f.type.startsWith('image/') ||
      /\.(pdf|png|jpe?g|webp|tiff?)$/i.test(f.name)
    if (!ok) {
      toast.error('Nur PDF oder Bilddateien werden unterstützt.')
      return
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error('Datei ist größer als 50 MB.')
      return
    }
    setFile(f)
    setResult(null)
  }

  const reset = () => {
    setFile(null)
    setResult(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const convert = async () => {
    if (!file) return
    setLoading(true)
    setResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch('/api/ki/pdf-to-md', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }))
        throw new Error(err.error || `HTTP ${res.status}`)
      }
      const data: ConvertResult = await res.json()
      setResult(data)
      toast.success(
        `Konvertiert: ${data.pageCount} Seite${data.pageCount === 1 ? '' : 'n'} in ${(
          data.elapsedMs / 1000
        ).toFixed(1)} s`
      )
    } catch (err: any) {
      toast.error(err.message || 'Fehler bei der Konvertierung')
    } finally {
      setLoading(false)
    }
  }

  const copyMarkdown = async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.markdown)
      setCopied(true)
      toast.success('Markdown in Zwischenablage kopiert')
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Kopieren fehlgeschlagen')
    }
  }

  const downloadMarkdown = () => {
    if (!result) return
    const baseName = (result.fileName || 'document').replace(/\.[^.]+$/, '')
    const blob = new Blob([result.markdown], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${baseName}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <PageHeader
        title="PDF → Markdown"
        description="PDF oder Scan hochladen, Text per OCR auslesen und als Markdown anzeigen, kopieren oder herunterladen. Die Datei wird nach der Verarbeitung sofort gelöscht."
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upload-Card */}
        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 text-lg font-semibold flex items-center gap-2">
              <FileUp className="h-5 w-5 text-primary-600" />
              Datei auswählen
            </h3>

            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />

            {!file ? (
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragOver(false)
                  pickFile(e.dataTransfer.files?.[0])
                }}
                className={`cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-colors ${
                  dragOver
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-muted-foreground/30 hover:border-primary-400 hover:bg-muted/50'
                }`}
              >
                <Upload className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                <p className="font-medium">PDF oder Bild hierher ziehen</p>
                <p className="text-sm text-muted-foreground">
                  oder <span className="underline">Datei auswählen</span> (max. 50 MB)
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <FileText className="h-6 w-6 shrink-0 text-primary-600" />
                    <div className="min-w-0">
                      <p className="truncate font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={reset}
                    className="ml-2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Entfernen"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <Button
                  className="w-full"
                  onClick={convert}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      OCR läuft… (CPU, kann 5–30 s dauern)
                    </>
                  ) : (
                    <>
                      <FileUp className="mr-2 h-4 w-4" />
                      In Markdown umwandeln
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground">
                  Die Datei wird nur temporär verarbeitet und auf dem Server sofort wieder
                  gelöscht.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ergebnis-Card */}
        <Card>
          <CardContent className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary-600" />
                Markdown
              </h3>
              {result && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyMarkdown}>
                    {copied ? (
                      <Check className="mr-2 h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="mr-2 h-4 w-4" />
                    )}
                    Kopieren
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadMarkdown}>
                    <Download className="mr-2 h-4 w-4" />
                    .md
                  </Button>
                </div>
              )}
            </div>

            {!result && !loading && (
              <div className="flex h-[420px] items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                Noch kein Ergebnis – links eine Datei hochladen.
              </div>
            )}

            {loading && (
              <div className="flex h-[420px] items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Verarbeite…
              </div>
            )}

            {result && (
              <>
                <div className="mb-2 text-xs text-muted-foreground">
                  {result.pageCount} Seite{result.pageCount === 1 ? '' : 'n'} ·{' '}
                  {(result.elapsedMs / 1000).toFixed(1)} s ·{' '}
                  {result.markdown.length.toLocaleString('de-DE')} Zeichen
                </div>
                <textarea
                  readOnly
                  value={result.markdown}
                  className="h-[420px] w-full resize-none rounded-lg border bg-muted/20 p-3 font-mono text-xs leading-relaxed"
                  spellCheck={false}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
